import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoIcon from "@mui/icons-material/Info";
import ReplayIcon from "@mui/icons-material/Replay";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import { Box, Button, Stack } from "@mui/material";
import {
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import fetcher from "../../../../dataProvider";
import { setLoading } from "../../../../redux/slices/customerJourney";
import { setExamDetails } from "../../../../redux/slices/examDetails";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { stepProps } from "../../../../types/common";
import {
  getCountryId,
  getDropdownData,
  getLanguageCode,
} from "../../../../utils/common";
import Select from "../../../common/FormComponents/Select";
import Popup from "../../../common/Modal";
import styles from "./styles";
import usePageView from "../../../../utils/analytics/usePageView";
import ExamList from "./ExamList";
import GTM from "../../../../utils/analytics/gtm";

const iconBinds: any = {
  Available: <DoneOutlinedIcon />,
  Limited: <InfoOutlinedIcon />,
  Full: <LockOutlinedIcon />,
  Closed: <LockOutlinedIcon />,
};

const ExamDetails = (props: stepProps) => {
  usePageView("Select Exam & Date");
  const customerJourney = useAppSelector((state) => state.customerJourney);
  const examDetails = useAppSelector((state) => state.examDetails);
  const { pageText, countryList } = customerJourney;
  const { examCenterDetails } = examDetails;
  const [tableData, setTableData] = useState<any>([]);
  const [warningBox, showWarningBox] = useState(false);
  const [lateFeeBox, showLateFeeBox] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [selectFilterType, setSelectFilterType] = useState("");
  const [filterDropdowndata, setExamDropdownsData] = useState<any>();
  const { toggleSteps } = props;
  const dispatch = useAppDispatch();

  const columns: GridColDef[] = [
    {
      field: "exam",
      flex: 1,
      minWidth: 100,
      headerName: pageText?.labelsMetadata?.examNameLabel,
    },
    {
      field: "date",
      flex: 1,
      minWidth: 100,
      headerName: pageText?.labelsMetadata?.dateLabel,
    },
    {
      field: "speakTestDate",
      flex: 1,
      minWidth: 200,
      headerName: pageText?.labelsMetadata?.speakingTestDateLabel,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Box className="table_header">
          {params?.colDef?.headerName}
          <Box className="tooltip">
            <InfoIcon />
            <Box className="tooltip_text">
              {pageText?.messagesMetadata?.speakingTestInfoMessage}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: "format",
      flex: 1,
      minWidth: 130,
      headerName: pageText?.labelsMetadata?.formatLabel,
    },
    {
      field: "price",
      flex: 1,
      minWidth: 100,
      headerName: pageText?.labelsMetadata?.priceLabel,
      renderCell: (params: GridRenderCellParams<any, any>) => {
        return (
          <Box>
            <Box sx={{ textAlign: "center" }}>
              {params.row?.fullDetail?.priceString}
            </Box>
            {params.row?.fullDetail?.lateExamFeeAddedMessage &&
              Number(params.row?.fullDetail?.lateFeePrice) !== 0 && (
                <Box sx={{ fontSize: "10px", color: "red" }}>
                  {params.row?.fullDetail?.lateExamFeeAddedMessage}
                </Box>
              )}
          </Box>
        );
      },
    },
    {
      field: "availability",
      flex: 1,
      minWidth: 130,
      headerName: pageText?.labelsMetadata?.availabilityLabel,
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <Box className={`status_cell status_cell--${params.formattedValue}`}>
          {iconBinds[params.formattedValue]}
          {params.formattedValue}
        </Box>
      ),
    },
    {
      field: "Slots Left",
      flex: 1,
      minWidth: 200,
      headerName: pageText?.labelsMetadata?.slotsLeft,
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <Box sx={{ textAlign: "center" }}>
          {params.row?.fullDetail?.placesAvailable}
        </Box>
      ),
    },
    {
      field: "action",
      flex: 1,
      minWidth: 100,
      headerName: "",
      sortable: false,
      renderCell: (params: GridRenderCellParams<any, any>) => {
        return !(
          params.row.availability === "Full" ||
          params.row.availability === "Closed"
        ) ? (
          <Box
            onClick={() => {
              sessionStorage.setItem(
                "examDetails",
                JSON.stringify(params.row.fullDetail)
              );
              if (
                params.row?.fullDetail?.lateExamFeeAddedMessage &&
                Number(params.row?.fullDetail?.lateFeePrice) !== 0 &&
                pageText?.labelsMetadata?.lateFeeInformationLabel
              ) {
                showLateFeeBox(true);
              } else {
                if (
                  params.row.fullDetail.showWarningMessage &&
                  params.row.fullDetail.warningMessage
                ) {
                  showWarningBox(true);
                  setWarningMessage(params.row.fullDetail.warningMessage);
                } else {
                  toggleSteps("termsAndConditions", 2);
                  GTM.addToCart(params.row.fullDetail);
                }
              }
            }}
            className="register_section"
          >
            {params.formattedValue} <ArrowForwardIosOutlinedIcon />
          </Box>
        ) : (
          <></>
        );
      },
    },
  ];

  const getExamFiltersDropdown = () => {
    if (examCenterDetails) {
      const { exams, examFormats, examDates, cities } = examCenterDetails;
      let examDropdown = getDropdownData(
        exams,
        pageText?.labelsMetadata?.selectText
      );
      let examFormatsDropdown = getDropdownData(
        examFormats,
        pageText?.labelsMetadata?.selectText
      );
      let datesDropdown = getDropdownData(
        examDates,
        pageText?.labelsMetadata?.selectText,
        "date"
      );
      let citiesDropdown = getDropdownData(
        cities,
        pageText?.labelsMetadata?.selectText
      );
      setExamDropdownsData({
        exams:
          selectFilterType === "exam"
            ? filterDropdowndata?.exams
            : examDropdown,
        examFormat:
          selectFilterType === "format"
            ? filterDropdowndata?.examFormat
            : examFormatsDropdown,
        examDates:
          selectFilterType === "examDate"
            ? filterDropdowndata?.examDates
            : datesDropdown,
        examCities:
          selectFilterType === "city"
            ? filterDropdowndata?.examCities
            : citiesDropdown,
      });
    }
  };

  const getExamFilterDetails = async (body: any) => {
    try {
      dispatch(setLoading({ loading: true }));
      const { data } = await fetcher.post("/api/Exam/", body);
      dispatch(setExamDetails({ details: data }));
      dispatch(setLoading({ loading: false }));
    } catch (e) {
      console.log("error", e);
      dispatch(setLoading({ loading: false }));
    }
  };

  useEffect(() => {
    if (countryList?.length) {
      const countryId = getCountryId(countryList);
      let examBody = {
        CountryId: countryId?.idValue,
        CityId: "",
        ExamId: "",
        ExamDate: "",
        ExamFormatId: "",
        LanguageCode: getLanguageCode(),
      };
      getExamFilterDetails(examBody);
      setSelectFilterType("");
      reset();
    }
  }, [pageText]);

  const getDate = (data: string) => {
    const date = new Date(data);
    return `${date.getDate() <= 9 ? "0" + date.getDate() : date.getDate()}/${
      date.getMonth() <= 8 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1
    }/${date.getFullYear()}`;
  };

  const getRows = (data: any) => {
    let row: any = [];
    data?.forEach((item: any) => {
      row.push({
        id: item.examId,
        exam: item.examName,
        date: getDate(item.examDate),
        speakTestDate: item.speakingTestDate,
        format: item.examFormat,
        price: `${item.currencyCode} ${item.price}`,
        availability: item.availability,
        slotsleft: item.placesAvailable,
        action: pageText?.buttonsMetadata?.registerButtonText,
        fullDetail: item,
      });
    });
    return row;
  };

  useEffect(() => {
    getExamFiltersDropdown();
    const data = examCenterDetails?.examCentres?.map((item: any) => {
      return { ...item, rows: getRows(item.exams), pageIndex: 0 };
    });
    setTableData(data);
  }, [examCenterDetails, pageText]);

  useEffect(() => {
    sessionStorage.removeItem("examDetails");
    sessionStorage.removeItem("examRulesData");
    sessionStorage.removeItem("candidateDetails");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const getSelectedExam = (data: any) => {
    const countryId = getCountryId(countryList);
    let examBody = {
      CountryId: countryId?.idValue,
      CityId: data?.city?.value || "",
      ExamId: data?.exam?.value || "",
      ExamDate: data?.date ? data?.date.value : "",
      ExamFormatId: data?.format ? data?.format?.value : "",
      LanguageCode: getLanguageCode(),
    };
    getExamFilterDetails(examBody);
  };

  const {
    control,
    handleSubmit,
    formState,
    setValue,
    getValues,
    reset,
    ...rest
  } = useForm({
    mode: "all",
    defaultValues: {
      exam: "",
      city: "",
      date: "",
      format: "",
    },
  });

  const { errors } = formState;

  const toggleFilters = () => {
    const values = getValues();
    getSelectedExam(values);
  };

  const resetFilters = () => {
    setSelectFilterType("");
    reset();
    toggleFilters();
  };

  const gotoPage = (page: number, entryIndex: number) => {
    const tempData = [...tableData];
    tempData[entryIndex].pageIndex = page;
    setTableData([...tempData]);
  };

  const handleClose = () => {
    showWarningBox(false);
  };

  return (
    <Box mt={11}>
      <Box sx={styles.filterBox}>
        <Box className="header">
          <Box className="filter_label">{pageText?.textMetadata?.filters}</Box>
          <Box className="reset_label">
            <ReplayIcon />
            <Box className="text" onClick={resetFilters}>
              {pageText?.buttonsMetadata?.resetFilters}
            </Box>
          </Box>
        </Box>
        <Grid
          container
          spacing={{ xs: 3, md: 4, xl: 6 }}
          mt={2}
          component="form"
        >
          <Grid xs={12} sm={6} md={3}>
            <Select
              classNamePrefix={"filter_drop"}
              placeholder={`${pageText?.labelsMetadata?.selectText}`}
              options={filterDropdowndata?.exams}
              control={control}
              onCustomChange={(e: any) => {
                setSelectFilterType("exam");
                if (!e.value) {
                  setSelectFilterType("");
                }
                toggleFilters();
              }}
              errors={errors}
              label={pageText?.labelsMetadata?.examNameLabel}
              subLabel={pageText?.labelsMetadata?.selectExamLabel}
              name="exam"
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Select
              classNamePrefix={"filter_drop"}
              placeholder={`${pageText?.labelsMetadata?.selectText}`}
              options={filterDropdowndata?.examCities}
              control={control}
              errors={errors}
              onCustomChange={(e: any) => {
                setSelectFilterType("city");
                if (!e.value) {
                  setSelectFilterType("");
                }
                toggleFilters();
              }}
              label={pageText?.labelsMetadata?.townCityLabel}
              subLabel={pageText?.labelsMetadata?.selectTownCityLabel}
              name="city"
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Select
              classNamePrefix={"filter_drop"}
              placeholder={`${pageText?.labelsMetadata?.selectText}`}
              options={filterDropdowndata?.examDates}
              control={control}
              errors={errors}
              onCustomChange={(e: any) => {
                setSelectFilterType("examDate");
                if (!e.value) {
                  setSelectFilterType("");
                }
                toggleFilters();
              }}
              label={pageText?.labelsMetadata?.dateLabel}
              subLabel={pageText?.labelsMetadata?.selectExamDateLabel}
              name="date"
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <Select
              classNamePrefix={"filter_drop"}
              placeholder={`${pageText?.labelsMetadata?.selectText}`}
              options={filterDropdowndata?.examFormat}
              control={control}
              errors={errors}
              onCustomChange={(e: any) => {
                setSelectFilterType("format");
                if (!e.value) {
                  setSelectFilterType("");
                }
                toggleFilters();
              }}
              label={pageText?.labelsMetadata?.formatLabel}
              subLabel={pageText?.labelsMetadata?.chooseExamFormatLabel}
              name="format"
            />
          </Grid>
        </Grid>
      </Box>
      {tableData?.map((item: any, index: number) => (
        <ExamList
          key={item.examCentreId}
          pageText={pageText}
          list={item}
          columns={columns}
          gotoPage={(page) => gotoPage(page, index)}
        />
      ))}
      <Stack direction="row" gap={2} pt={3} mb={10}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIosIcon />}
          onClick={() => toggleSteps("location", 0)}
        >
          {pageText?.buttonsMetadata?.previousStep}
        </Button>
        <Button variant="text" onClick={() => toggleSteps("location", 0)}>
          {pageText?.buttonsMetadata?.cancelButtonText}
        </Button>
      </Stack>
      <Popup open={warningBox} handleClose={handleClose}>
        <Box>
          <Box sx={styles.modalBody}>
            <Box
              sx={{ wordBreak: "break-word" }}
              dangerouslySetInnerHTML={{
                __html: warningMessage,
              }}
            ></Box>
          </Box>
          <Stack direction="row" justifyContent="center" gap={3}>
            <Button
              variant="outlined"
              onClick={() => {
                let examDetails: any = sessionStorage.getItem("examDetails");
                examDetails = JSON.parse(examDetails);

                handleClose();
                toggleSteps("termsAndConditions", 2);
                GTM.addToCart(examDetails);
              }}
            >
              {pageText?.buttonsMetadata?.okButtonText}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                handleClose();
              }}
            >
              {pageText?.buttonsMetadata?.cancelButtonText}
            </Button>
          </Stack>
        </Box>
      </Popup>
      <Popup open={lateFeeBox} handleClose={handleClose}>
        <Box>
          <Box sx={styles.modalBody}>
            <Box>{pageText?.labelsMetadata?.lateFeeInformationLabel}</Box>
          </Box>
          <Stack direction="row" justifyContent="center" gap={3}>
            <Button
              variant="outlined"
              onClick={() => {
                let examDetails: any = sessionStorage.getItem("examDetails");
                examDetails = JSON.parse(examDetails);
                if (
                  examDetails?.showWarningMessage &&
                  examDetails?.warningMessage
                ) {
                  showLateFeeBox(false);
                  showWarningBox(true);
                  setWarningMessage(examDetails?.warningMessage);
                } else {
                  showLateFeeBox(false);
                  toggleSteps("termsAndConditions", 2);
                  GTM.addToCart(examDetails);
                }
              }}
            >
              {pageText?.buttonsMetadata?.okButtonText}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                showLateFeeBox(false);
              }}
            >
              {pageText?.buttonsMetadata?.cancelButtonText}
            </Button>
          </Stack>
        </Box>
      </Popup>
    </Box>
  );
};

export default ExamDetails;
